import { HttpError } from '@littlebird/auth-client';

import { backendApis } from '@/backendApis';
import { userStore } from '@/stores/user.store';

import { electronApis } from '../electron-api';

import { meetingStore } from './meetings.state';

import type {
  MeetingPostProcessingRunningState,
  RunningPostProcessingLedgerFields,
} from './meetings.durable-map';
import type { RecordingSession } from './meetings.recording-session';

export type FinalizedPostProcessingRound = NonNullable<
  MeetingPostProcessingRunningState['finalized']
>;

export function buildRoundLedgerFields({
  meetingId,
  replaceWatermark,
  expectedLength,
  deferSummary = false,
}: {
  meetingId: string;
  replaceWatermark: number | null;
  expectedLength: number;
  deferSummary?: boolean;
}): RunningPostProcessingLedgerFields {
  const record = meetingStore.getPostProcessingJob(meetingId);
  const unfinalized = record?.state === 'running' ? record : undefined;
  return {
    // Preserve an inherited unknown boundary; guessing can overwrite earlier speech
    replaceFromOffset:
      unfinalized && replaceWatermark !== null
        ? unfinalized.replaceFromOffset
        : replaceWatermark,
    sessionEpoch: (unfinalized?.sessionEpoch ?? 0) + 1,
    processingToken: crypto.randomUUID(),
    expectedLength,
    deferSummary,
  };
}

export async function beginTranscriptProcessingRound(
  meetingId: string,
  ledger: RunningPostProcessingLedgerFields,
  session?: RecordingSession
) {
  if (ledger.processingToken) {
    await backendApis.meetingNotes.beginTranscriptProcessing(
      meetingId,
      ledger.processingToken,
      ledger.expectedLength,
      session
    );
  }
}

export function completeTranscriptProcessingRound(
  meetingId: string,
  ledger: Pick<
    MeetingPostProcessingRunningState,
    | 'replaceFromOffset'
    | 'sessionEpoch'
    | 'processingToken'
    | 'expectedLength'
    | 'deferSummary'
  >,
  finalized: FinalizedPostProcessingRound
) {
  const record = meetingStore.getPostProcessingJob(meetingId);
  if (
    !record ||
    record.userId !== userStore.userId ||
    record.sessionEpoch !== ledger.sessionEpoch ||
    record.processingToken !== ledger.processingToken
  )
    return;
  // Recovery resumes release and acknowledgement, never the committed replacement
  return meetingStore.setDurablePostProcessingJobState({
    ...ledger,
    state: 'running',
    meetingId,
    startedAt:
      record.state === 'running' ? record.startedAt : new Date().toISOString(),
    finalized,
    releasePending: true,
  });
}

export async function releaseTranscriptProcessingRound(meetingId: string) {
  const record = meetingStore.getPostProcessingJob(meetingId);
  if (record?.state !== 'running' || record.userId !== userStore.userId) return;
  if (record.finalized && meetingStore.isMeetingIdRecording(meetingId)) return;
  if (!record.processingToken && !record.finalized) {
    meetingStore.clearPostProcessingJob(meetingId);
    return;
  }
  const persisted = meetingStore.setDurablePostProcessingJobState({
    ...record,
    releasePending: true,
  });
  if (!persisted) return;
  const pending = meetingStore.getPostProcessingJob(meetingId);
  const isCurrent = () =>
    record.userId === userStore.userId &&
    meetingStore.getPostProcessingJob(meetingId) === pending;
  try {
    if (
      record.finalized &&
      !meetingStore.setLastFinalizedBoundary(
        meetingId,
        record.finalized.boundary
      )
    )
      return;
    const roundReleased =
      !record.processingToken ||
      (await finishRoundOnServer(meetingId, record.processingToken));
    if (!isCurrent()) return;
    if (record.finalized) {
      if (
        record.processingToken &&
        meetingStore.interruptedRecordings.get(meetingId)?.processingToken ===
          record.processingToken
      ) {
        meetingStore.clearInterruptedRecording(meetingId);
      }
      // A failed acknowledgement keeps the job pending, so the next reconcile
      // sends finish again even after a 404, and retries the acknowledgement
      await electronApis.meetings.acknowledgePostProcessingResult({
        meetingId,
      });
      if (!isCurrent()) return;
    }
    meetingStore.clearPostProcessingJob(meetingId);
    if (roundReleased) return record;
    // A round the server no longer holds was not finished: no summary follows,
    // and the caller's finished-round effects must not run
    meetingStore.setSummaryFinalizing(meetingId, false);
  } catch (error) {
    console.warn(
      '[meetings] Failed to release transcript processing; reconciliation will retry:',
      error
    );
  }
}

/** Returns false when the server answers 404: the meeting was deleted,
 * merged away, or is not this account's, so the round has nothing left to
 * release, and a retry would get the same answer forever */
async function finishRoundOnServer(meetingId: string, processingToken: string) {
  try {
    await backendApis.meetingNotes.finishTranscriptProcessing(
      meetingId,
      processingToken
    );
    return true;
  } catch (error) {
    if (!(error instanceof HttpError && error.status === 404)) throw error;
    console.info(
      `[meetings] Transcript processing finish answered 404 for meeting ${meetingId}; dropping the round`
    );
    return false;
  }
}
